import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CubeIcon, DocumentPlusIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { fetchPatientCloudFiles, type PatientCloudFile } from '../../../../services/patientFiles';
import { getFilePreviewType } from '@odo/shared-ui';
import { SIGNED_URL_GC_TIME_MS } from '../../../../providers/queryClient';

export const CARD_STALE_MS = 60_000;

/** Spark requires a minimum set of records; the Photos step is locked below this. */
export const MIN_SPARK_PHOTOS = 3;

/** Max STL size we'll auto-download to the browser for a card thumbnail. */
export const STL_PREVIEW_MAX_BYTES = 5 * 1024 * 1024;

export type StepId = 'case' | 'refinement' | 'photos' | 'scans';

export interface StepMeta {
    id: StepId;
    label: string;
    icon: ComponentType<{ className?: string }>;
}

export const STEPS: StepMeta[] = [
    { id: 'case', label: 'Case', icon: DocumentPlusIcon },
    { id: 'photos', label: 'Photos', icon: PhotoIcon },
    { id: 'scans', label: 'Scans', icon: CubeIcon }
];

/** Refinement wizard steps — same Photos/Scans as the primary flow, but the
 *  first step creates a *refinement* off the parent case instead of a primary case. */
export const REFINEMENT_STEPS: StepMeta[] = [
    { id: 'refinement', label: 'Refinement', icon: DocumentPlusIcon },
    { id: 'photos', label: 'Photos', icon: PhotoIcon },
    { id: 'scans', label: 'Scans', icon: CubeIcon }
];

/**
 * Each step registers its single primary action here; the wizard renders it in
 * the footer's bottom-right corner (mirroring the bottom-left Back button).
 */
export interface FooterAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
}
export type RegisterFooter = (action: FooterAction | null) => void;

/** A step reports whether it has a non-interruptible upload in flight, so the
 *  wizard can lock step navigation while files are being sent. */
export type RegisterBusy = (busy: boolean) => void;

/** Short, human-readable upload date for file pickers/lists (e.g. "Jun 26, 2026"). */
export function formatUploadDate(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Stable signature of a position→file map, to detect changes since last upload. */
export function photosSignature(assignments: Record<string, string>): string {
    return Object.entries(assignments)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([position, fileId]) => `${position}:${fileId}`)
        .join('|');
}

/** Infer the dental arch from an STL filename, for the scan card badge/label. */
export function archOf(filename: string): { short: string | null; label: string } {
    const n = filename.toLowerCase();
    if (n.includes('maxillary') || n.includes('upper'))
        return { short: 'UPPER', label: 'Maxillary arch (upper)' };
    if (n.includes('mandibular') || n.includes('lower'))
        return { short: 'LOWER', label: 'Mandibular arch (lower)' };
    return { short: null, label: 'STL scan' };
}

/** Patient cloud files filtered to a preview kind (images for photos, stl for scans). */
export function useCloudFiles(patientId: string, kind: 'image' | 'stl') {
    return useQuery({
        queryKey: ['patient-cloud-files', patientId],
        queryFn: () => fetchPatientCloudFiles(patientId),
        staleTime: CARD_STALE_MS,
        gcTime: SIGNED_URL_GC_TIME_MS,
        enabled: Boolean(patientId),
        select: (all: PatientCloudFile[]) =>
            all.filter((f) => getFilePreviewType(f.file.originalFilename) === kind)
    });
}
